import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';

export default function LeaveBalanceWidget() {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchBalance();
  }, []);

  const fetchBalance = async () => {
    try {
      const res = await api.get('/leaves/my-balance');
      setBalance(res.data);
    } catch (err) {
      console.error('Failed to load leave balance', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-border">
        <div className="grid-cell p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !balance) return null;

  const totalTaken = balance.by_type?.reduce((sum, lt) => sum + (lt.taken || 0), 0) || 0;
  const totalMax = balance.by_type?.reduce((sum, lt) => sum + (lt.max_days || 0), 0) || 0;
  const totalRemaining = balance.by_type?.reduce((sum, lt) => sum + (lt.remaining || 0), 0) || 0;
  const overallProgress = totalMax > 0 ? (totalTaken / totalMax) * 100 : 0;

  return (
    <div className="border border-border">
      <Link to="/leaves" className="grid-cell p-6 hover:bg-muted transition-colors block">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-bold leading-tight">Leave Balance {balance.current_year}</div>
          <span className="text-2xl">🏖️</span>
        </div>
        
        <div className="text-xs text-muted-foreground mb-4">
          {totalTaken} of {totalMax} days used
        </div>

        {/* Progress bar like projects */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs font-mono-mini mb-1">
            <span>{Math.round(overallProgress)}%</span>
            <span>{totalTaken}/{totalMax} days</span>
          </div>
          <div className="w-full h-1 bg-secondary">
            <div 
              className={`h-1 ${overallProgress > 80 ? 'bg-orange-500' : 'bg-[#002FA7]'}`}
              style={{ width: `${Math.min(overallProgress, 100)}%` }} 
            />
          </div>
        </div>

        {/* Type breakdown - compact */}
        {balance.by_type && balance.by_type.length > 0 && (
          <div className="space-y-2">
            {balance.by_type.slice(0, 3).map((lt, idx) => {
              const percentage = lt.max_days > 0 ? (lt.taken / lt.max_days) * 100 : 0;
              
              return (
                <div key={idx} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div 
                      className="w-2 h-2 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: lt.color }}
                    />
                    <span className="truncate text-muted-foreground">{lt.type_label}</span>
                  </div>
                  <span className="font-mono-mini ml-2 flex-shrink-0">
                    {lt.remaining}/{lt.max_days}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {(!balance.by_type || balance.by_type.length === 0) && (
          <div className="text-xs text-muted-foreground text-center py-2">
            No leave types configured
          </div>
        )}
      </Link>
    </div>
  );
}
